import React from 'react';

export interface Option {
  value: string;
  label: string;
}

export interface RadioGroupFieldProps {
  label: string;
  value: string | null;
  onChange: (value: string) => void;
  options: readonly Option[];
  error?: string;
  required?: boolean;
  name?: string;
  disabled?: boolean;
}

/**
 * Reusable radio button group field component
 */
export const RadioGroupField: React.FC<RadioGroupFieldProps> = ({
  label,
  value,
  onChange,
  options,
  error,
  required = false,
  name,
  disabled = false,
}) => {
  const fieldName = name || `radio-group-${label.toLowerCase().replace(/\s+/g, '-')}`;
  const fieldsetId = `${fieldName}-fieldset`;
  const errorId = `${fieldName}-error`;

  return (
    <div style={{ marginBottom: '15px' }}>
      <fieldset
        id={fieldsetId}
        style={{
          padding: '15px',
          border: error ? '1px solid #dc3545' : '1px solid #ccc',
          borderRadius: '4px',
        }}
        aria-invalid={error ? 'true' : 'false'}
        aria-describedby={error ? errorId : undefined}
      >
        <legend style={{ fontWeight: required ? 'bold' : 'normal', padding: '0 8px' }}>
          {label}
          {required && <span style={{ color: 'red', marginLeft: '4px' }} aria-label="required">*</span>}
        </legend>
        {options.map((option) => {
          const optionId = `${fieldName}-${option.value}`;
          const isChecked = value === option.value;
          
          return (
            <label
              key={option.value}
              htmlFor={optionId}
              style={{
                display: 'block',
                marginBottom: '8px',
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.6 : 1,
              }}
            >
              <input
                id={optionId}
                type="radio"
                name={fieldName}
                value={option.value}
                checked={isChecked}
                onChange={() => !disabled && onChange(option.value)}
                required={required}
                disabled={disabled}
                style={{ marginRight: '8px' }}
              />
              {option.label}
            </label>
          );
        })}
      </fieldset>
      {error && (
        <div
          id={errorId}
          role="alert"
          style={{
            color: '#dc3545',
            fontSize: '12px',
            marginTop: '4px',
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
};

